// src/components/charts/DonutChart.tsx
//
// ★★★ WAVE 27 — PART-TO-WHOLE, and the one form this repository had already
// refused once.
//
// ⚠ **`StatusBarChart` REFUSED A DONUT AND WAS RIGHT TO.** Its own header records
// why: it compares five lifecycle stages whose counts are routinely within a few
// percent of each other, and `anti-patterns.md` refuses a donut for comparing close
// values — an angle is the hardest encoding to compare precisely, so two slices a
// few percent apart are indistinguishable.
//
// ⚠ **SO WHY ONE HERE.** This is used for a different job on different data: a
// small set of slices with genuinely DISTINCT magnitudes, where the question is
// "roughly what is this made of" rather than "which of these two is bigger". That
// is the one thing a donut does better than a bar — it shows the whole as a whole.
// The caller is responsible for that being true, and the guard is the same one the
// bar chart uses: the exact figures are one click away in the table.
//
// ⚠ **A HARD CEILING OF SIX SLICES, enforced rather than advised.** Past that,
// angles become unreadable and the legend becomes the chart. A seventh category is
// the caller's problem to fold into "Other" before it gets here — this component
// refuses instead of quietly cycling a palette it does not own.
//
// ⚠ **THE CENTRE CARRIES THE TOTAL**, which is the whole reason to leave a hole:
// a pie with a hole and nothing in it is a pie that wasted its middle.
import type { ReactElement } from 'react'
import { ChartFrame, type ChartLegendEntry } from './ChartFrame'
import { MarkNoteBubble, MarkNoteLayer, markProps, useMarkNote } from './mark-interaction'

export interface DonutSlice {
  readonly label: string
  readonly slot: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  readonly value: number
  /**
   * ★ The caller's own identifier for this slice — the value a filter uses,
   * which is deliberately NOT the label.
   *
   * "New bill" is what a reader sees; `NEW` is what `/bills?billType=` accepts.
   * A drill-down built from the label would send a display string to a filter
   * and quietly match nothing.
   */
  readonly key?: string
}

export const MAX_DONUT_SLICES = 6

const SIZE = 220
const RADIUS = 84
const THICKNESS = 30

/** A slice as an SVG arc. Angles run clockwise from twelve o'clock. */
function arc(startFraction: number, endFraction: number): string {
  const centre = SIZE / 2
  const toPoint = (fraction: number, r: number): readonly [number, number] => {
    const angle = fraction * Math.PI * 2 - Math.PI / 2
    return [centre + r * Math.cos(angle), centre + r * Math.sin(angle)]
  }
  const outer = RADIUS
  const inner = RADIUS - THICKNESS
  const [x1, y1] = toPoint(startFraction, outer)
  const [x2, y2] = toPoint(endFraction, outer)
  const [x3, y3] = toPoint(endFraction, inner)
  const [x4, y4] = toPoint(startFraction, inner)
  const large = endFraction - startFraction > 0.5 ? 1 : 0
  return [
    `M ${x1} ${y1}`,
    `A ${outer} ${outer} 0 ${large} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${inner} ${inner} 0 ${large} 0 ${x4} ${y4}`,
    'Z',
  ].join(' ')
}

export function DonutChart({
  title,
  subtitle,
  slices,
  centreLabel,
  onSelect,
}: {
  readonly title: string
  readonly subtitle: string
  readonly slices: readonly DonutSlice[]
  /** What the number in the hole is OF — "bills", "records". */
  readonly centreLabel: string
  /**
   * ★★★ Called with the slice the reader pressed — the sector IS the control.
   *
   * ⚠ The chart does not route. The page passes a callback that navigates, so
   * this component stays a drawing and the destination stays where the filters
   * are known. Omit it and the sectors are inert, which is correct for a chart
   * whose categories have no screen to open.
   */
  readonly onSelect?: (slice: DonutSlice) => void
}): ReactElement {
  if (slices.length > MAX_DONUT_SLICES) {
    throw new Error(
      `DonutChart takes at most ${MAX_DONUT_SLICES} slices and was given ${slices.length}. ` +
        'Fold the tail into "Other" before rendering: past six, the angles are unreadable and the ' +
        'legend becomes the chart.',
    )
  }

  const total = slices.reduce((sum, s) => sum + s.value, 0)
  const legend: ChartLegendEntry[] = slices.map((s) => ({ label: s.label, slot: s.slot }))
  const rows = slices.map((s) => ({
    label: s.label,
    value: s.value,
    // The share is stated in the table rather than on the chart: a percentage
    // label on every slice is the "number on every point" §5.3 rule 6 warns off.
    share: total === 0 ? 'Not applicable' : `${((s.value / total) * 100).toFixed(1)}%`,
  }))

  const { note, show, hide } = useMarkNote()

  let cursor = 0
  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      legend={legend}
      columns={[
        { key: 'label', header: 'Category' },
        { key: 'value', header: 'Count', numeric: true },
        { key: 'share', header: 'Share', numeric: true },
      ]}
      rows={rows}
      emptyMessage="Nothing in your scope falls into any of these categories yet."
    >
      <MarkNoteLayer>
      <div className="pt-chart-plot flex items-center justify-center">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="pt-donut"
          role="img"
          aria-label={`${title}. ${subtitle}. The figures are available as a table.`}
        >
          {slices.map((slice) => {
            const fraction = total === 0 ? 0 : slice.value / total
            const start = cursor
            cursor += fraction
            if (fraction === 0) return null
            const value = `${slice.value.toLocaleString('en-SG')} ${centreLabel}`
            return (
              <path
                key={slice.label}
                d={arc(start, cursor)}
                {...markProps({
                  className: `pt-series-fill-${slice.slot} pt-donut-slice`,
                  note: { label: slice.label, value },
                  ...(onSelect === undefined ? {} : { onSelect: () => onSelect(slice) }),
                  show,
                  hide,
                })}
              />
            )
          })}
          {/* The hole earns itself. */}
          <text className="pt-donut-total" x={SIZE / 2} y={SIZE / 2} textAnchor="middle">
            {total.toLocaleString('en-SG')}
          </text>
          <text className="pt-donut-caption" x={SIZE / 2} y={SIZE / 2 + 18} textAnchor="middle">
            {centreLabel}
          </text>
        </svg>
      </div>
      <MarkNoteBubble note={note} />
      </MarkNoteLayer>
    </ChartFrame>
  )
}
